import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, MenuItem, Typography, Alert } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { nurseService } from "src/api/nurse/nurseService";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useTranslate } from "src/locales";

interface ServiceAddEgfrModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data: { height: string; muscleFactor: string }) => void;
  patientId?: number;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const ServiceAddEgfrModal: React.FC<ServiceAddEgfrModalProps> = ({
  open,
  onClose,
  onSave,
  patientId,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  const [height, setHeight] = useState("");
  const [muscleFactor, setMuscleFactor] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const fetchPatientHeight = async () => {
      if (!open || !patientId) return;
      try {
        setLoading(true);
        const patientData: any = await nurseService.patient.getPatientInfo({
          id: patientId,
        });
        const data = patientData?.data || patientData;
        if (data?.height) {
          setHeight(String(data.height));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPatientHeight();
  }, [open, patientId]);

  // EGFR form tanımını AdditionalInfo/fields üzerinden yükle
  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        const res = await referenceService.additionalInfo.getFields({
          formtype: "EGFR",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        setAdditionalInfo(res);

        const fields = res.fields || [];
        const factorField = fields.find(
          (f) => f.fieldname === "PatientMuscleFactor"
        );
        if (factorField && Array.isArray(factorField.options)) {
          setOptions(
            factorField.options.map((opt) => ({
              label: opt.label ?? "",
              value: String(opt.value),
            }))
          );
        } else {
          // Fallback: sabit liste
          setOptions([
            { label: "Bir yaş altı prematüre bebek (0,33)", value: "0.33" },
            { label: "Bir yaş altı bebek (0,45)", value: "0.45" },
            { label: "Çocuk ve/veya Adolesan kız (0,55)", value: "0.55" },
            { label: "Adolesan Erkek (0,7)", value: "0.7" },
          ]);
        }
      } catch {
        // Hata durumunda mevcut manuel akış devam etsin; sabit listeye düş
        setOptions([
          { label: "Bir yaş altı prematüre bebek (0,33)", value: "0.33" },
          { label: "Bir yaş altı bebek (0,45)", value: "0.45" },
          { label: "Çocuk ve/veya Adolesan kız (0,55)", value: "0.55" },
          { label: "Adolesan Erkek (0,7)", value: "0.7" },
        ]);
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    if (!height) {
      setError(t("patient-request.egfr.errors.height-required"));
      return;
    }
    setError("");
    onSave?.({ height, muscleFactor });
    onClose();
  };

  const handleClose = () => {
    setError("");
    onClose();
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.egfr.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading || !height} />
          <ByzMuiCloseButton onClick={handleClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.egfr.height")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiTextField
              fullWidth
              size="small"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              required
              type="number"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.egfr.muscle-factor")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.egfr.muscle-factor")}
              value={
                options.find((opt) => opt.value === muscleFactor)?.label || ""
              }
              selectedId={muscleFactor || undefined}
              dialogMaxHeight="60vh"
              staticData={options.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  setMuscleFactor(String(first.id));
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default ServiceAddEgfrModal;


